import * as React from "react";
import authModule from "../../../../../../utilities/AuthModule";
import { FileIconType, getFileTypeIconAsUrl, IFileTypeIconOptions } from "@fluentui/react-file-type-icons";
import { SharePointFolderContentTypeIds } from "../../../../config/constants";
import { fetchWithRetry } from "../../../../../../utilities/FetchRetryUtil";
import { Spinner, SpinnerSize } from "@fluentui/react";

const TILE_LOADER_STYLES: React.CSSProperties = {
    position: "absolute",
    top: 5,
    left: 5
};

const DEFAULT_IMAGE_STYLE: React.CSSProperties = {
    objectFit: "contain",
    marginBottom: 7,
    height: 165,
    borderRadius: 3
};

const DEFAULT_DETAIL_IMAGE_STYLE: React.CSSProperties = {
    ...DEFAULT_IMAGE_STYLE,
    height: 200
};

const SHAREPOINT_THUMBNAIL_SUFFIX = "/thumbnails/0/c512x432/content?prefer=noredirect,closestavailablesize";
const FS_OBJECT_TYPE_FOLDER = "1";
const DEFAULT_ICON_SIZE = 48;
const DEFAULT_ICON_IMAGE_TYPE: "svg" | "png" = "svg";
const THUMBNAIL_LOAD_TIMEOUT = 300;

const getDefaultItemTypeImageUrl = (viewItem?: any): string | undefined => {
    let specificIconParams: Partial<Pick<IFileTypeIconOptions, "type" | "extension">> = { type: FileIconType.genericFile };

    if (viewItem) {
        const { ContentTypeId, ".fileType": fileType, FSObjType } = viewItem;

        const isDocumentSet = ContentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet);
        const isFolderContentType = ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder);
        const isFsObjTypeFolder = FSObjType === FS_OBJECT_TYPE_FOLDER;

        if (isDocumentSet) {
            specificIconParams = { type: FileIconType.docset };
        } else if (isFolderContentType || isFsObjTypeFolder) {
            specificIconParams = { type: FileIconType.folder };
        } else if (fileType) {
            specificIconParams = { extension: fileType };
        }
    }

    try {
        return getFileTypeIconAsUrl({ size: DEFAULT_ICON_SIZE, imageFileType: DEFAULT_ICON_IMAGE_TYPE, ...specificIconParams });
    } catch (error) {
        return undefined;
    }
};

interface ViewTileImageRenderProps {
    viewItem: any;
    isDetailTile: boolean;
    imageClassname: string;
}

export const ViewTileImageRenderer: React.FC<ViewTileImageRenderProps> = React.memo(
    ({ viewItem, isDetailTile, imageClassname }) => {
        const canAttemptInitialFetch = React.useMemo(() => {
            return viewItem && viewItem?.FSObjType !== FS_OBJECT_TYPE_FOLDER && !!viewItem[".spItemUrl"];
        }, [viewItem]);

        const [isLoading, setIsLoading] = React.useState<boolean>(canAttemptInitialFetch);
        const [thumbImageUrl, setThumbImageUrl] = React.useState<string | undefined>();
        const [showDefaultImage, setShowDefaultImage] = React.useState<boolean>(!canAttemptInitialFetch);

        const defaultImageUrl = React.useMemo(() => getDefaultItemTypeImageUrl(viewItem), [viewItem]);
        const objectUrlRef = React.useRef<string | undefined>(undefined);
        const timeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);

        const fetchThumbnail = React.useCallback(async (itemSpUrl: string): Promise<void> => {
            setIsLoading(true);
            setShowDefaultImage(false);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = undefined;
            }

            timeoutRef.current = setTimeout(() => {
                setShowDefaultImage(true);
            }, THUMBNAIL_LOAD_TIMEOUT);

            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = undefined;
            }

            try {
                const response: Response = await fetchWithRetry(itemSpUrl + SHAREPOINT_THUMBNAIL_SUFFIX, {
                    headers: {
                        Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`,
                    },
                });

                if (response.ok) {
                    const blob = await response.blob();
                    const newObjectUrl = URL.createObjectURL(blob);
                    objectUrlRef.current = newObjectUrl;
                    setThumbImageUrl(newObjectUrl);

                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = undefined;
                    }
                    setShowDefaultImage(false);
                } else {
                    setShowDefaultImage(true);
                }
            } catch (err) {
                setShowDefaultImage(true);
            } finally {
                setIsLoading(false);
            }
        }, []);

        React.useEffect(() => {
            const itemUrlString = viewItem && viewItem[".spItemUrl"];

            if (canAttemptInitialFetch && itemUrlString) {
                try {
                    const url = new URL(itemUrlString);
                    fetchThumbnail(`${url.origin}${url.pathname}`);
                } catch (error) {
                    setIsLoading(false);
                    setShowDefaultImage(true);
                }
            } else {
                setIsLoading(false);
                setShowDefaultImage(true);
            }

            return () => {
                if (objectUrlRef.current) {
                    URL.revokeObjectURL(objectUrlRef.current);
                    objectUrlRef.current = undefined;
                }

                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = undefined;
                }
            };
        }, [viewItem, canAttemptInitialFetch, fetchThumbnail]);

        return (
            <>
                {isLoading && <Spinner size={SpinnerSize.small} style={TILE_LOADER_STYLES} />}
                {!thumbImageUrl && !showDefaultImage
                    ? <div style={isDetailTile ? DEFAULT_DETAIL_IMAGE_STYLE : DEFAULT_IMAGE_STYLE}></div>
                    : <img
                        alt={viewItem?.FileLeafRef}
                        src={thumbImageUrl || defaultImageUrl}
                        className={imageClassname}
                        style={!thumbImageUrl || showDefaultImage ? (isDetailTile ? DEFAULT_DETAIL_IMAGE_STYLE : DEFAULT_IMAGE_STYLE) : undefined}
                    />
                }
            </>
        );
    }
);